import React, { useState } from "react";
import { motion } from "motion/react";
import noiseImage from "@/assets/images/shop/noise.webp";
import { H2 } from "@/shared/ui/typographies/h2";
import { P } from "@/shared/ui/typographies/paragraph";
import { cn } from "@/shared/utils/style.util";

export const NoiseCardTitle = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <H2 className={cn("max-w-80 text-left text-balance font-semibold tracking-[-0.015em] text-white leading-11", className)}>
      {children}
    </H2>
  );
};

export const NoiseCardParagraph = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => {
  return (
    <P className={cn("mt-4 max-w-[26rem] text-left text-white", className)}>
      {children}
    </P>
  );
};

export const NoiseCard = ({
  children,
  containerClassName,
  containerStyle,
  className,
}: {
  children: React.ReactNode
  containerClassName?: string
  containerStyle?: React.CSSProperties
  className?: string
}) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseMove = (event: React.MouseEvent<HTMLElement>) => {
    const { clientX, clientY } = event;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = (clientX - (rect.left + rect.width / 2)) / 27;
    const y = (clientY - (rect.top + rect.height / 2)) / 27;
    setMousePosition({ x, y });
  };

  return (
    <motion.section
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        setMousePosition({ x: 0, y: 0 });
      }}
      style={{
        transform: isHovering
          ? `translate3d(${mousePosition.x}px, ${mousePosition.y}px, 0) scale3d(1, 1, 1)`
          : "translate3d(0px, 0px, 0) scale3d(1, 1, 1)",
        transition: "transform 0.1s ease-out",
        ...containerStyle,
      }}
      className={cn(
        "mx-auto w-full relative rounded-2xl overflow-hidden",
        containerClassName,
      )}
    >
      <div
        className="relative h-full [background-image:radial-gradient(88%_100%_at_top,rgba(255,255,255,0.5),rgba(255,255,255,0))]  sm:mx-0 sm:rounded-2xl overflow-hidden"
        style={{
          boxShadow:
            "0 10px 32px rgba(34, 42, 53, 0.12), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.05), 0 4px 6px rgba(34, 42, 53, 0.08), 0 24px 108px rgba(47, 48, 55, 0.10)",
        }}
      >
        <motion.div
          style={{
            transform: isHovering
              ? `translate3d(${-mousePosition.x}px, ${-mousePosition.y}px, 0) scale3d(1.02, 1.02, 1)`
              : "translate3d(0px, 0px, 0) scale3d(1, 1, 1)",
            transition: "transform 0.1s ease-out",
          }}
          className={cn("h-full px-4 py-20 sm:px-10", className)}
        >
          <Noise />
          {children}
        </motion.div>
      </div>
    </motion.section>
  );
};

const Noise = () => {
  return (
    <div
      className="absolute inset-0 w-full h-full scale-[1.2] transform opacity-10 [mask-image:radial-gradient(#fff,transparent,75%)]"
      style={{
        backgroundImage: `url(${noiseImage})`,
        backgroundSize: "30%",
      }}
    />
  );
};
